export const VISIBILITY_LABELS = {
  public_listing: "Public — appears in catalog",
  unlisted: "Unlisted — direct link only",
  private: "Private — staff only",
};

export const isClassEditable = (status) => status !== "canceled";

const zoned = (value, timeZone, options) => (
  value ? new Date(value).toLocaleString("en-US", { timeZone: timeZone || undefined, ...options }) : null
);

const zoneName = (value, timeZone) => zoned(value, timeZone, { timeZoneName: "short" }).split(" ").pop();

export const formatDate = (value, timeZone) => zoned(value, timeZone, { month: "short", day: "numeric", year: "numeric" });

export const formatDateTime = (value, timeZone) => zoned(value, timeZone, {
  month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short",
});

export const formatAdded = (value) => (value ? new Date(value).toLocaleDateString("en-US", { month: "short", year: "numeric" }) : "—");

export const formatMonthYear = (value, timeZone) => zoned(value, timeZone, { month: "long", year: "numeric" });

const calendarDay = (value, timeZone) => {
  const [month, day, year] = zoned(value, timeZone, { year: "numeric", month: "2-digit", day: "2-digit" }).split("/").map(Number);
  return Date.UTC(year, month - 1, day) / 86400000;
};

export const calendarDaysBetween = (from, to, timeZone) => calendarDay(to, timeZone) - calendarDay(from, timeZone);

export const sessionDay = (value, timeZone) => zoned(value, timeZone, { weekday: "short", month: "short", day: "numeric" });

const clockTime = (value, timeZone) => zoned(value, timeZone, { hour: "numeric", minute: "2-digit" });

const sameCalendarDay = (startsAt, endsAt, timeZone) => (
  zoned(startsAt, timeZone, { dateStyle: "short" }) === zoned(endsAt, timeZone, { dateStyle: "short" })
);

const endLabel = (startsAt, endsAt, timeZone) => (sameCalendarDay(startsAt, endsAt, timeZone)
  ? clockTime(endsAt, timeZone)
  : zoned(endsAt, timeZone, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }));

export function sessionTimeRange(startsAt, endsAt, timeZone) {
  if (!startsAt) return "";
  const start = clockTime(startsAt, timeZone);
  if (!endsAt) return start;
  const end = endLabel(startsAt, endsAt, timeZone);
  if (!sameCalendarDay(startsAt, endsAt, timeZone)) return `${start} – ${end}`;
  const [startClock, startPeriod] = start.split(" ");
  const [, endPeriod] = end.split(" ");
  return startPeriod === endPeriod ? `${startClock} – ${end}` : `${start} – ${end}`;
}

export function sessionTimeRangeWithZone(startsAt, endsAt, timeZone) {
  if (!startsAt) return "";
  const startZone = zoneName(startsAt, timeZone);
  const endZone = zoneName(endsAt || startsAt, timeZone);
  if (startZone === endZone) return `${sessionTimeRange(startsAt, endsAt, timeZone)} ${endZone}`;
  return `${clockTime(startsAt, timeZone)} ${startZone} – ${endLabel(startsAt, endsAt, timeZone)} ${endZone}`;
}

export function formatSessionRange(startsAt, endsAt, timeZone) {
  if (!startsAt) return "Not scheduled";
  return `${sessionDay(startsAt, timeZone)}, ${sessionTimeRangeWithZone(startsAt, endsAt, timeZone)}`;
}

export function formatDateRange(from, to, timeZone) {
  if (!from) return "—";
  const zone = timeZone || undefined;
  const full = { month: "short", day: "numeric", year: "numeric", timeZone: zone };
  const end = new Date(to || from).toLocaleDateString("en-US", full);
  const start = new Date(from).toLocaleDateString("en-US", full);
  if (start === end) return start;
  const sameYear = new Date(from).toLocaleDateString("en-US", { year: "numeric", timeZone: zone })
    === new Date(to).toLocaleDateString("en-US", { year: "numeric", timeZone: zone });
  const shortStart = sameYear ? new Date(from).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: zone }) : start;
  return `${shortStart} – ${end}`;
}

export const formatRegisteredAt = (value, timeZone) => {
  if (!value) return "—";
  const sameYear = zoned(value, timeZone, { year: "numeric" }) === zoned(new Date(), timeZone, { year: "numeric" });
  return zoned(value, timeZone, { month: "short", day: "numeric", ...(sameYear ? {} : { year: "numeric" }), hour: "numeric", minute: "2-digit" });
};
